import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Package, FileText, Truck, BookOpenText, SpinnerGap } from "@phosphor-icons/react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  ingredient: { icon: Package, label: "Ingrediente", color: "text-blue-600", bg: "bg-blue-50" },
  sheet: { icon: FileText, label: "Ficha Técnica", color: "text-emerald-600", bg: "bg-emerald-50" },
  supplier: { icon: Truck, label: "Fornecedor", color: "text-amber-600", bg: "bg-amber-50" },
  menu: { icon: BookOpenText, label: "Cardápio", color: "text-purple-600", bg: "bg-purple-50" },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLanguage();

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/search`, { params: { q } });
      setResults(data.results || []);
      setSelectedIdx(-1);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (query.trim().length >= 2) {
      debounceRef.current = setTimeout(() => search(query.trim()), 300);
    } else {
      setResults([]);
    }
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (result) => {
    navigate(result.path);
    setQuery("");
    setIsOpen(false);
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && selectedIdx >= 0 && results[selectedIdx]) {
      handleSelect(results[selectedIdx]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const showDropdown = isOpen && query.length >= 2;

  return (
    <div ref={containerRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('search_placeholder') || "Buscar ingredientes, fichas, fornecedores..."}
          className="w-full h-9 pl-9 pr-3 text-sm bg-zinc-100 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:bg-white placeholder:text-zinc-400 transition-colors"
          data-testid="global-search-input"
        />
        {loading && <SpinnerGap size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-zinc-400" />}
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-xl z-[80] max-h-[320px] overflow-y-auto" data-testid="search-results-dropdown">
          {results.length === 0 && !loading && (
            <div className="px-4 py-6 text-center">
              <MagnifyingGlass size={24} className="mx-auto text-zinc-300 mb-2" />
              <p className="text-xs text-zinc-400">{t('search_no_results') || "Nenhum resultado encontrado"}</p>
            </div>
          )}
          {results.map((result, idx) => {
            const config = TYPE_CONFIG[result.type] || TYPE_CONFIG.ingredient;
            const Icon = config.icon;
            return (
              <button
                key={`${result.type}-${result.id}`}
                onClick={() => handleSelect(result)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors ${idx === selectedIdx ? "bg-zinc-50" : ""} ${idx > 0 ? "border-t border-zinc-50" : ""}`}
                data-testid={`search-result-${idx}`}
              >
                <div className={`w-7 h-7 rounded-md ${config.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={14} className={config.color} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">{result.name}</p>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider ${config.color}`}>{config.label}</span>
                    {result.detail && <span className="text-[10px] text-zinc-400">· {result.detail}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
